import { Card, CardContent, CardHeader } from "@/shared/components/ui/card";
import { H3 } from "@/shared/components/typography";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import {
  Area,
  AreaList,
  Building,
  BuildingList,
} from "@/services/backend/qpm/buildings/types/building";
import { BuildingSelector } from "@/services/backend/qpm/buildings/components/building-selector";
import { Button } from "@/shared/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { BuildingLevelsComponent } from "@/routes/processes/[processId]/qpm/manage-building/_components/building-levels-component";
import { AddBuildingComponent } from "@/services/backend/qpm/buildings/components/add-building";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { BuildingScopedAreasComponent } from "@/routes/processes/[processId]/qpm/manage-building/_components/building-scoped-areas-component";
import {
  AreaType,
  LevelType,
} from "@/services/backend/qpm/buildings/types/building-enum";

export type ManageBuildingBuildingsSectionProps = {
  buildingList: BuildingList | undefined;
  selectedBuilding: Building | undefined;
  onBuildingSelect: (buildingId: string) => void;
  onBuildingCreated: (building: Building) => void;
  onDeleteBuilding: (buildingId: string) => void;
  customerId: string;
  serviceObjectId: string;
};

export function ManageBuildingBuildingsSection({
  buildingList,
  selectedBuilding,
  onBuildingSelect,
  onBuildingCreated,
  onDeleteBuilding,
  customerId,
  serviceObjectId,
}: ManageBuildingBuildingsSectionProps) {
  const showNoBuildingsHint =
    !buildingList ||
    !buildingList.buildings ||
    buildingList.buildings.length <= 0;

  return (
    <Card>
      <CardHeader>
        <H3>{t("Gebäude und Bereiche")}</H3>
      </CardHeader>
      <CardContent>
        <PersistenceContextProvider>
          <div className="flex items-end justify-between">
            <div className="flex items-end gap-2">
              <BuildingSelector
                buildingList={buildingList}
                selectedBuilding={selectedBuilding}
                handleBuildingSelect={onBuildingSelect}
                customerId={customerId}
                serviceObjectId={serviceObjectId}
                onBuildingCreateCallback={onBuildingCreated}
              />
              {selectedBuilding &&
                selectedBuilding.levels &&
                selectedBuilding.levels.length === 0 &&
                (!selectedBuilding.unlevelledAreas ||
                  selectedBuilding.unlevelledAreas.length === 0) && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="icon">
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {t("Gebäude löschen?")}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {t(
                            `Möchten Sie das Gebäude (${selectedBuilding.name}) unwiderruflich löschen?`,
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("Abbruch")}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDeleteBuilding(selectedBuilding.id)}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          {t("Bestätigen")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
            </div>
            <PersistenceStateUsingContext className="mr-2" />
          </div>

          {selectedBuilding && (
            <div key={selectedBuilding.id} className="mt-3 flex flex-col gap-2">
              <Tabs defaultValue="inside" className="gap-0">
                <TabsList className="grid w-full grid-cols-2 rounded-none">
                  <TabsTrigger value="inside">{t("Innenbereiche")}</TabsTrigger>
                  <TabsTrigger value="outside">
                    {t("Außenbereiche")}
                  </TabsTrigger>
                </TabsList>
                <TabsContent
                  value="inside"
                  className="m-0 overflow-clip border"
                >
                  <BuildingScopedAreasComponent
                    buildingId={selectedBuilding.id}
                    areas={collectBuildingScopedInsideAreas(selectedBuilding)}
                    areaType={AreaType.AreaTypeInside}
                    hint={t(
                      "Innenbereiche können ohne Ebene direkt am Gebäude angelegt werden.",
                    )}
                    createLabel={t("Innenbereich anlegen")}
                    createDescription={t(
                      "Geben Sie hier den Namen des neuen Innenbereichs ein.",
                    )}
                    emptyText={t(
                      "Es sind noch keine gebäudeweiten Innenbereiche vorhanden.",
                    )}
                  />
                  <BuildingLevelsComponent
                    buildingId={selectedBuilding.id}
                    levelList={collectInsideLevels(selectedBuilding)}
                    levelledAreas={selectedBuilding.levelledAreas || []}
                    levelType={LevelType.LevelTypeInside}
                  />
                </TabsContent>
                <TabsContent value="outside" className="m-0 border">
                  <BuildingScopedAreasComponent
                    buildingId={selectedBuilding.id}
                    areas={collectBuildingScopedOutsideAreas(selectedBuilding)}
                    areaType={AreaType.AreaTypeOutside}
                    hint={t(
                      "Außenbereiche können mit oder ohne Ebene angelegt werden.",
                    )}
                    createLabel={t("Außenbereich anlegen")}
                    createDescription={t(
                      "Geben Sie hier den Namen des neuen Außenbereichs ein.",
                    )}
                    emptyText={t("Es sind noch keine Außenbereiche vorhanden.")}
                  />
                  <BuildingLevelsComponent
                    buildingId={selectedBuilding.id}
                    levelList={collectOutsideLevels(selectedBuilding)}
                    levelledAreas={selectedBuilding.levelledAreas || []}
                    levelType={LevelType.LevelTypeOutside}
                    areaType={AreaType.AreaTypeOutside}
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}

          {showNoBuildingsHint && (
            <div className="flex flex-col gap-2 p-2">
              <span>
                {t(
                  "Es sind keine Gebäude vorhanden. Bitte legen Sie zuerst ein Gebäude an.",
                )}
              </span>
              <AddBuildingComponent
                customerId={customerId}
                onBuildingCreate={onBuildingCreated}
                serviceObjectId={serviceObjectId}
              />
            </div>
          )}
        </PersistenceContextProvider>
      </CardContent>
    </Card>
  );
}

function collectBuildingScopedInsideAreas(
  selectedBuilding: Building,
): AreaList {
  const areas = new Map<string, Area>();
  (selectedBuilding.unlevelledAreas || []).forEach((area) => {
    if (area.type !== AreaType.AreaTypeOutside) {
      areas.set(area.base.id, area);
    }
  });
  return Array.from(areas.values());
}

function collectBuildingScopedOutsideAreas(
  selectedBuilding: Building,
): AreaList {
  const outsideAreas = new Map<string, Area>();

  (selectedBuilding.unlevelledAreas || []).forEach((area) => {
    if (area.type === AreaType.AreaTypeOutside) {
      outsideAreas.set(area.base.id, area);
    }
  });

  return Array.from(outsideAreas.values());
}

function collectInsideLevels(selectedBuilding: Building) {
  return (selectedBuilding.levels || []).filter(
    (level) =>
      level.type === LevelType.LevelTypeInside ||
      level.type === LevelType.LevelTypeUndefined,
  );
}

function collectOutsideLevels(selectedBuilding: Building) {
  return (selectedBuilding.levels || []).filter(
    (level) => level.type === LevelType.LevelTypeOutside,
  );
}
